import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import flowchLogo from "../../assets/logo_principal_horizontal.png";
import { AuthContext } from "../../context/Auth/AuthContext";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const barHeights = [32, 48, 36, 68, 82, 56, 74, 92, 64, 50, 78, 42];

const Login = () => {
  const { handleLogin } = useContext(AuthContext);

  const [user, setUser] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [toast, setToast] = useState({ show: false, message: "" });
  const toastTimeoutRef = useRef(null);

  const isEmbedMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("embed") === "true";
  }, []);

  const emailIsValid = emailRegex.test(user.email.trim());
  const canSubmit = emailIsValid && user.password.trim().length > 0 && !isSubmitting;

  useEffect(() => {
    document.title = "Flowch Talk";
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const showToast = message => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setToast({ show: true, message });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: "" });
    }, 2500);
  };

  const handleChangeInput = event => {
    const { name, value } = event.target;
    setUser(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = event => {
    const { name } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    setTouched({ email: true, password: true });
    if (!emailIsValid || !user.password.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await handleLogin({
        email: user.email.trim(),
        password: user.password,
        rememberMe
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap");

        :root {
          --bg: #080b14;
          --bg-2: #0f1727;
          --card: rgba(16, 21, 36, 0.78);
          --border: rgba(255,255,255,0.08);
          --text: #f8fafc;
          --muted: #8b93a7;
          --purple: #8b5cf6;
          --purple-2: #a855f7;
          --blue: #38bdf8;
          --green: #4ade80;
          --orange: #ff8a00;
          --orange-2: #ffb020;
          --radius: 26px;
          --shadow: 0 30px 80px rgba(0,0,0,0.42);
        }

        * {
          box-sizing: border-box;
        }

        .flowch-login-root {
          margin: 0;
          color: var(--text);
          background:
            radial-gradient(circle at top left, rgba(139,92,246,0.18), transparent 28%),
            radial-gradient(circle at top right, rgba(56,189,248,0.10), transparent 24%),
            var(--bg);
          overflow-x: hidden;
          font-family: "Inter", "Segoe UI", Roboto, Arial, sans-serif;
          min-height: 100vh;
        }

        .noise {
          position: fixed;
          inset: 0;
          pointer-events: none;
          opacity: 0.2;
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 54px 54px;
          z-index: 0;
        }

        .layout {
          position: relative;
          z-index: 1;
        }

        .orange-text {
          color: var(--orange);
          font-style: normal;
        }

        .hero-section {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1.2fr 440px;
          gap: 44px;
          align-items: center;
          padding: 44px 34px 90px;
        }

        .hero-content {
          max-width: 760px;
        }

        .flowch-logo {
          width: 352px;
          height: auto;
          display: block;
          filter: brightness(1.12);
        }

        .hero-logo {
          margin-bottom: 42px;
        }

        .login-logo {
          width: 296px;
          height: auto;
          display: block;
          filter: brightness(1.12);
        }

        .pill {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 999px;
          border: 1px solid rgba(139,92,246,0.28);
          background: rgba(139,92,246,0.10);
          color: #d8c7ff;
          font-size: 13px;
          font-weight: 800;
        }

        .pill span,
        .dashboard-status span,
        .online-status span {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: var(--green);
          box-shadow: 0 0 0 6px rgba(74,222,128,0.1);
        }

        .hero-title {
          margin: 28px 0 0;
          font-size: clamp(46px, 7vw, 82px);
          line-height: 0.92;
          letter-spacing: -0.08em;
          max-width: 760px;
        }

        .hero-title span {
          background: linear-gradient(90deg, #fff, #c4b5fd 45%, var(--orange-2));
          -webkit-background-clip: text;
          color: transparent;
        }

        .hero-title em {
          color: var(--orange);
          -webkit-text-fill-color: var(--orange);
          font-style: normal;
        }

        .hero-content p {
          max-width: 560px;
          margin: 24px 0 0;
          color: #a3adbf;
          line-height: 1.7;
          font-size: 18px;
        }

        .hero-actions {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          margin-top: 34px;
        }

        .primary-btn,
        .ghost-btn {
          height: 50px;
          padding: 0 20px;
          border-radius: 16px;
          font-weight: 800;
          cursor: pointer;
          transition: 0.22s ease;
          border: 0;
          color: white;
        }

        .primary-btn {
          background: linear-gradient(135deg, var(--purple), var(--purple-2));
          box-shadow: 0 20px 40px rgba(139,92,246,0.28);
        }

        .ghost-btn {
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.05);
        }

        .primary-btn:hover,
        .ghost-btn:hover {
          transform: translateY(-2px);
        }

        .hero-proof {
          display: flex;
          gap: 18px;
          flex-wrap: wrap;
          margin-top: 44px;
        }

        .hero-proof div {
          padding: 16px 18px;
          border-radius: 18px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.04);
          color: var(--muted);
          font-size: 13px;
        }

        .hero-proof strong {
          display: block;
          color: var(--text);
          margin-bottom: 6px;
        }

        .login-panel {
          position: sticky;
          top: 40px;
        }

        .login-card,
        .dashboard-card,
        .feature-card,
        .segment-card,
        .cta-card {
          border: 1px solid var(--border);
          background: linear-gradient(180deg, rgba(17,24,39,0.88), rgba(12,17,29,0.84));
          box-shadow: var(--shadow);
          backdrop-filter: blur(18px);
        }

        .login-card {
          padding: 28px;
          border-radius: 30px;
        }

        .login-top {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          gap: 14px;
        }

        .online-status,
        .dashboard-status {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #bbf7d0;
          font-size: 12px;
          font-weight: 800;
        }

        .login-card h2 {
          margin: 26px 0 8px;
          font-size: 30px;
          letter-spacing: -0.05em;
        }

        .login-card p {
          margin: 0;
          color: var(--muted);
          font-size: 14px;
        }

        .login-form {
          display: grid;
          gap: 8px;
          margin-top: 24px;
        }

        .login-form input {
          height: 52px;
          border-radius: 15px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.05);
          color: white;
          padding: 0 16px;
          outline: none;
        }

        .login-form input:focus {
          border-color: rgba(139,92,246,0.55);
          box-shadow: 0 0 0 4px rgba(139,92,246,0.12);
        }

        .full {
          width: 100%;
        }

        .login-error {
          min-height: 16px;
          margin: 2px 0 0;
          font-size: 12px;
          color: #fca5a5;
        }

        .flowch-remember {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 4px 0 4px;
          color: #cbd5e1;
          font-size: 13px;
        }

        .flowch-remember input {
          width: 16px;
          height: 16px;
          accent-color: #8b5cf6;
        }

        .login-links {
          display: flex;
          justify-content: space-between;
          margin-top: 16px;
          gap: 10px;
        }

        .login-links a {
          color: #c4b5fd;
          text-decoration: none;
          font-size: 13px;
          font-weight: 700;
        }

        .demo-box {
          margin-top: 20px;
          padding: 18px;
          border-radius: 20px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border);
        }

        .demo-box strong {
          display: block;
          margin-bottom: 6px;
        }

        .demo-box span {
          display: block;
          color: var(--muted);
          font-size: 13px;
          margin-bottom: 14px;
        }

        .demo-box button {
          width: 100%;
          height: 44px;
          border-radius: 14px;
          border: 1px solid rgba(139,92,246,0.24);
          background: rgba(139,92,246,0.12);
          color: #d8c7ff;
          font-weight: 800;
          cursor: pointer;
        }

        .feature-section,
        .dashboard-section,
        .segment-section,
        .cta-section {
          padding: 0 34px 90px;
        }

        .section-title {
          margin-bottom: 30px;
        }

        .section-title.center {
          text-align: center;
        }

        .section-title small,
        .dashboard-header small,
        .cta-card small {
          color: #c4b5fd;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-size: 12px;
          font-weight: 800;
        }

        .section-title h3,
        .dashboard-header h3,
        .cta-card h3 {
          margin: 12px 0 0;
          font-size: clamp(30px, 4vw, 46px);
          letter-spacing: -0.06em;
          max-width: 700px;
          line-height: 1;
        }

        .feature-grid,
        .segment-grid {
          display: grid;
          gap: 16px;
        }

        .feature-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .segment-grid {
          grid-template-columns: repeat(4, 1fr);
        }

        .feature-card,
        .segment-card {
          padding: 24px;
          border-radius: 24px;
        }

        .feature-card.contrast {
          background: linear-gradient(135deg, rgba(139,92,246,0.24), rgba(255,138,0,0.08), rgba(17,24,39,0.92));
        }

        .feature-card.contrast-2 {
          background: linear-gradient(135deg, rgba(56,189,248,0.16), rgba(17,24,39,0.92));
        }

        .feature-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 14px;
          background: rgba(255,255,255,0.08);
          color: #d8c7ff;
          font-weight: 900;
          margin-bottom: 18px;
        }

        .feature-card h4,
        .segment-card strong {
          display: block;
          margin-bottom: 10px;
          font-size: 18px;
        }

        .feature-card p,
        .segment-card span,
        .cta-card p {
          margin: 0;
          color: var(--muted);
          line-height: 1.6;
        }

        .dashboard-card {
          padding: 28px;
          border-radius: 34px;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          align-items: center;
          margin-bottom: 30px;
        }

        .dashboard-body {
          display: grid;
          grid-template-columns: 260px 1fr 280px;
          gap: 18px;
        }

        .metric-column {
          display: grid;
          gap: 16px;
        }

        .metric-card,
        .insight-card {
          padding: 22px;
          border-radius: 24px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
        }

        .metric-card.dark {
          background: linear-gradient(135deg, rgba(139,92,246,0.16), rgba(255,255,255,0.04));
        }

        .metric-card small,
        .insight-card small {
          display: block;
          margin-bottom: 10px;
          color: var(--muted);
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 800;
        }

        .metric-card strong {
          display: block;
          margin-bottom: 8px;
          font-size: 42px;
          letter-spacing: -0.06em;
        }

        .metric-card span {
          color: var(--green);
          font-size: 13px;
          font-weight: 700;
        }

        .chart-area {
          padding: 22px;
          border-radius: 28px;
          background: radial-gradient(circle at top right, rgba(139,92,246,0.18), transparent 42%), rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 26px;
        }

        .chart-header strong {
          font-size: 16px;
        }

        .chart-header span {
          color: var(--green);
          font-size: 13px;
          font-weight: 700;
        }

        .bars {
          height: 240px;
          display: flex;
          align-items: end;
          gap: 10px;
        }

        .bar {
          flex: 1;
          border-radius: 999px 999px 4px 4px;
          background: linear-gradient(180deg, #a78bfa, rgba(139,92,246,0.12));
          animation: grow 1.2s ease both;
        }

        @keyframes grow {
          from { height: 10%; opacity: 0.4; }
        }

        .insight-card ul {
          padding-left: 18px;
          margin: 0;
          display: grid;
          gap: 14px;
          color: #d5dceb;
          line-height: 1.5;
        }

        .segment-card.highlight {
          background: linear-gradient(135deg, rgba(255,138,0,0.16), rgba(139,92,246,0.18));
        }

        .cta-card {
          padding: 48px;
          border-radius: 34px;
          text-align: center;
          background: linear-gradient(135deg, rgba(139,92,246,0.16), rgba(255,138,0,0.08), rgba(17,24,39,0.92));
        }

        .cta-card p {
          max-width: 620px;
          margin: 20px auto 0;
        }

        .center-actions {
          justify-content: center;
        }

        .toast {
          position: fixed;
          right: 24px;
          bottom: 24px;
          z-index: 30;
          padding: 14px 16px;
          border-radius: 16px;
          border: 1px solid var(--border);
          background: rgba(17,24,39,0.94);
          box-shadow: var(--shadow);
          transform: translateY(20px);
          opacity: 0;
          transition: 0.25s ease;
          pointer-events: none;
        }

        .toast.show {
          transform: translateY(0);
          opacity: 1;
        }

        .flowch-login-root.embed .hero-section {
          grid-template-columns: 1fr;
          min-height: 100vh;
          padding: 20px;
          place-items: center;
        }

        .flowch-login-root.embed .hero-content,
        .flowch-login-root.embed .feature-section,
        .flowch-login-root.embed .dashboard-section,
        .flowch-login-root.embed .segment-section,
        .flowch-login-root.embed .cta-section,
        .flowch-login-root.embed .noise {
          display: none;
        }

        .flowch-login-root.embed .login-panel {
          position: static;
          width: min(100%, 460px);
        }

        @media (max-width: 1180px) {
          .hero-section {
            display: flex;
            flex-direction: column-reverse;
            align-items: stretch;
          }

          .hero-section,
          .dashboard-body,
          .feature-grid,
          .segment-grid {
            grid-template-columns: 1fr;
          }

          .login-panel {
            position: relative;
            top: auto;
          }
        }

        @media (max-width: 760px) {
          .hero-section,
          .feature-section,
          .dashboard-section,
          .segment-section,
          .cta-section {
            padding-left: 22px;
            padding-right: 22px;
          }

          .hero-title {
            font-size: 42px;
          }

          .hero-logo {
            margin: 10px 0 28px;
            width: 280px;
          }

          .hero-content p {
            font-size: 16px;
          }

          .hero-actions {
            display: grid;
          }

          .primary-btn,
          .ghost-btn {
            width: 100%;
          }

          .dashboard-card,
          .cta-card,
          .login-card {
            padding: 22px;
          }

          .section-title h3,
          .dashboard-header h3,
          .cta-card h3 {
            font-size: 36px;
          }

          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <div className={`flowch-login-root ${isEmbedMode ? "embed" : ""}`}>
        <div className="noise" />

        <main className="layout">
          <section className="hero-section">
            <div className="hero-content">
              <img className="flowch-logo hero-logo" src={flowchLogo} alt="Flowch" />

              <h1 className="hero-title">
                Transforme conversas em
                <span> operação <em>inteligente.</em></span>
              </h1>

              <p>
                Centralize canais, automatize atendimentos e acompanhe sua operação em <strong className="orange-text">tempo real</strong>.
              </p>

              <div className="hero-actions">
                <button className="primary-btn" type="button" onClick={() => showToast("CTA de demonstração acionado.")}>Solicitar demonstração</button>
                <button
                  className="ghost-btn"
                  type="button"
                  onClick={() => {
                    const section = document.getElementById("produto");
                    if (section) {
                      section.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  Conhecer plataforma
                </button>
              </div>

              <div className="hero-proof">
                <div>
                  <strong>API Oficial</strong>
                  WhatsApp
                </div>
                <div>
                  <strong>Tempo real</strong>
                  operacional
                </div>
                <div>
                  <strong>IA integrada</strong>
                  ao atendimento
                </div>
              </div>
            </div>

            <aside className="login-panel">
              <div className="login-card">
                <div className="login-top">
                  <img className="login-logo" src={flowchLogo} alt="Flowch" />
                </div>

                <h2>Entrar no <span className="orange-text">Flowch Talk</span></h2>
                <p>Acesse sua central operacional.</p>

                <form className="login-form" noValidate onSubmit={handleSubmit}>
                  <input
                    name="email"
                    type="email"
                    value={user.email}
                    onChange={handleChangeInput}
                    onBlur={handleBlur}
                    placeholder="E-mail"
                    autoComplete="email"
                    required
                  />
                  <p className="login-error">{touched.email && !emailIsValid ? "Informe um e-mail valido." : ""}</p>

                  <input
                    name="password"
                    type="password"
                    value={user.password}
                    onChange={handleChangeInput}
                    onBlur={handleBlur}
                    placeholder="Senha"
                    autoComplete="current-password"
                    required
                  />
                  <p className="login-error">{touched.password && !user.password.trim() ? "Informe sua senha." : ""}</p>

                  <label className="flowch-remember" htmlFor="rememberMe">
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={event => setRememberMe(event.target.checked)}
                    />
                    Confiar neste navegador
                  </label>

                  <button type="submit" className="primary-btn full" disabled={!canSubmit}>
                    {isSubmitting ? "Entrando..." : "Entrar"}
                  </button>
                </form>

                <div className="login-links">
                  <a href="#">Esqueci minha senha</a>
                  <RouterLink to="/signup">Criar conta</RouterLink>
                </div>

                <div className="demo-box">
                  <strong>Conhecendo o Flowch Talk?</strong>
                  <span>Solicite uma demonstração guiada.</span>
                  <button type="button" onClick={() => showToast("CTA de demonstração acionado.")}>Agendar demonstração</button>
                </div>
              </div>
            </aside>
          </section>

          <section className="feature-section" id="produto">
            <div className="section-title">
              <small>Fluxo operacional</small>
              <h3>Uma central viva para atendimento e <span className="orange-text">operação.</span></h3>
            </div>

            <div className="feature-grid">
              <div className="feature-card contrast">
                <div className="feature-icon">◎</div>
                <h4>Omnichannel</h4>
                <p>WhatsApp, Instagram, Telegram e Webchat em um unico ambiente.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">✦</div>
                <h4>IA integrada</h4>
                <p>Insights, automacoes e apoio operacional em tempo real.</p>
              </div>

              <div className="feature-card contrast-2">
                <div className="feature-icon">▣</div>
                <h4>Operação em tempo real</h4>
                <p>Indicadores claros para agir rápido e reduzir gargalos.</p>
              </div>
            </div>
          </section>

          <section className="dashboard-section" id="operacao">
            <div className="dashboard-card">
              <div className="dashboard-header">
                <div>
                  <small>Central operacional</small>
                  <h3>Visibilidade operacional em <span className="orange-text">tempo real.</span></h3>
                </div>
              </div>

              <div className="dashboard-body">
                <div className="metric-column">
                  <div className="metric-card">
                    <small>Backlog ativo</small>
                    <strong>1.284</strong>
                    <span>+12% nas últimas 2h</span>
                  </div>

                  <div className="metric-card dark">
                    <small>Não lidos</small>
                    <strong>94</strong>
                    <span>23 aguardando há +15 min</span>
                  </div>
                </div>

                <div className="chart-area">
                  <div className="chart-header">
                    <strong>Volume operacional</strong>
                    <span>Pico às 14h</span>
                  </div>

                  <div className="bars">
                    {barHeights.map((height, index) => (
                      <span
                        key={`${height}-${index}`}
                        className="bar"
                        style={{ height: `${height}%`, animationDelay: `${index * 0.05}s` }}
                      />
                    ))}
                  </div>
                </div>

                <div className="insight-card">
                  <small>Insights IA</small>
                  <ul>
                    <li>Fila Financeiro aumentou 28%</li>
                    <li>Tempo de espera acima do normal</li>
                    <li>Instagram com baixa atividade</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="segment-section" id="segmentos">
            <div className="section-title center">
              <small>Segmentos</small>
              <h3>Flexível para diferentes <span className="orange-text">operações.</span></h3>
            </div>

            <div className="segment-grid">
              <div className="segment-card">
                <strong>Saúde</strong>
                <span>Agendamentos, confirmação e atendimento.</span>
              </div>

              <div className="segment-card highlight">
                <strong>Industria</strong>
                <span>Solicitações internas e suporte operacional.</span>
              </div>

              <div className="segment-card">
                <strong>Serviços</strong>
                <span>Atendimento centralizado e produtividade.</span>
              </div>

              <div className="segment-card">
                <strong>Atendimento interno</strong>
                <span>RH, financeiro, TI e operacoes.</span>
              </div>
            </div>
          </section>

          <section className="cta-section" id="demo">
            <div className="cta-card">
              <small>Flowch Talk</small>
              <h3>Transforme atendimento em operacao <span className="orange-text">inteligente.</span></h3>
              <p>Centralize canais, automatize rotinas e acompanhe tudo em tempo real.</p>

              <div className="hero-actions center-actions">
                <button className="primary-btn" type="button" onClick={() => showToast("CTA de demonstracao acionado.")}>Solicitar demonstracao</button>
                <button className="ghost-btn" type="button" onClick={() => showToast("Especialista acionado.")}>Falar com especialista</button>
              </div>
            </div>
          </section>
        </main>

        <div className={`toast ${toast.show ? "show" : ""}`}>{toast.message}</div>
      </div>
    </>
  );
};

export default Login;
